import _ from 'lodash'
import { ApolloClient, ApolloLink } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { InMemoryCache } from '@apollo/client/cache'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { RestLink } from 'apollo-link-rest'
import { createStore, applyMiddleware, combineReducers, compose } from 'redux'
import { createLogger } from 'redux-logger'
import thunk from 'redux-thunk'
import urljoin from 'url-join'

import auth, { getAuthIdToken, refreshToken } from '~/redux-modules/auth'
import form from '~/redux-modules/form'
import preferences, {
    LOGIN_PATH,
    SAVE_TYPES,
} from '~/redux-modules/preferences'
import estimates from '~/redux-modules/estimates'
import identity, { getUserIdentity } from '~/redux-modules/identity'
import modals from '~/redux-modules/modals'
import quotes from '~/redux-modules/quotes'
import qmrViewerReducer from '~/redux-modules/qmr-viewer'
import { createScrollMiddleware } from '~/redux-modules/scroll'
import { driftMiddleware } from '~/redux-modules/drift'
import { reducer as graphqlReducer } from '~/redux-modules/graphql'
import { reducer as dfmLayersReducer } from '~/redux-modules/dfm-layers'
import { reducer as quoteDetailReducer } from '~/redux-modules/quote-detail'
import { reducer as dfmMessagesReducer } from '~/redux-modules/dfm-messages'
import { reducer as imDfmReducer } from '~/redux-modules/im-dfm'
import { reducer as queryRefactorReducer } from '~/redux-modules/query-refactor'
import { reducer as quoteQlltReducer } from '~/redux-modules/quote-qllt'
import { reducer as unsavedImDfm3dSlidesReducer } from '~/redux-modules/unsaved-im-dfm-3d-slides'
import userDetailsReducer from '~/redux-modules/user-details'
import quoteModelRevisionReducer from '~/redux-modules/quote-model-revision'
import shippingAddressHTQuoteReducer from '~/redux-modules/shipping-address-HT-quote'
import { getenv } from '~/settings'
import { isTokenExpired } from '~/lib/jwtHelper'
import ApiClient from '~/lib/ApiClient'
import logger from '~/lib/logger'
import createFictivHandler from '~/lib/analytics/handlers/fictiv'
import createDriftHandler from '~/lib/analytics/handlers/drift'
import createMunchkinHandler from '~/lib/analytics/handlers/munchkin'
import createAppcuesHandler from '~/lib/analytics/handlers/appcues'
import createHeapHandler from '~/lib/analytics/handlers/heap'
import createRouterTrackingMiddleware from '~/lib/analytics/middlewares/createRouterTrackingMiddleware'
import DriftApi from '~/lib/apis/DriftApi'
import MunchkinApi from '~/lib/apis/MunchkinApi'
import AppcuesApi from '~/lib/apis/AppcuesApi'
import { HeapApi } from '~/lib/apis'
import { createAnalyticsMiddleware } from '~/lib/analytics'
import {
    LocalStorageEngine,
    createStorageMiddleware,
} from '~/redux-modules/storage'
import ModalSessionTimedOut from '~/apps/consumer/components/ModalSessionTimedOut'

import { introspectionQueryResultData } from './graphqlSchema'

export const isProduction = getenv('SENTRY_CURRENT_ENV') === 'production'

function createApolloLink({ store }) {
    const { dispatch, getState } = store

    const tokenContext = setContext(async (request, prevContext) => {
        try {
            const state = getState()

            let accessToken = getAuthIdToken(state)

            // If we don't have a valid access token, then
            // try one time to renew the token.
            if (!accessToken || isTokenExpired(accessToken)) {
                accessToken = await dispatch(refreshToken())

                // And if we still don't have one, then the user
                // needs to re-login.
                if (!accessToken) {
                    window.history.push(LOGIN_PATH)
                    throw new Error('Missing valid graphql credentials.')
                }
            }

            const bearerToken = `Bearer ${accessToken}`

            return {
                ...prevContext,
                headers: {
                    ...prevContext.headers,
                    Authorization: bearerToken,
                },
            }
        } catch (error) {
            // This means  auth has failed
            dispatch(ModalSessionTimedOut.open())
            throw error
        }
    })
    const trueUserContext = setContext((request, prevContext) => {
        const currentUser = getUserIdentity(getState())
        const ctx = { ...prevContext }

        if (currentUser && currentUser.trueUser) {
            _.set(ctx, 'headers.X-Impersonation', currentUser.id)
        }

        return ctx
    })
    const errorLink = onError(({ networkError }) => {
        const FAILED_AUTH_CODE = 401

        if (networkError?.statusCode === FAILED_AUTH_CODE) {
            dispatch(ModalSessionTimedOut.open())
        }
    })

    const base = getenv('FICTIV_API_BASE')

    return ApolloLink.from([
        tokenContext,
        trueUserContext,
        new RestLink({
            uri: urljoin(base, '/api/v1/'), // default uri
            endpoints: {
                v1: {
                    uri: urljoin(base, '/api/v1/'),
                    responseTransformer: response => {
                        //this is for a 404, ApolloRestLink returns data:null and error:undefined for 404s
                        if (response) {
                            return response.json().then(data => {
                                //this is so that the data can be nested under any keyword (data.data or data.items)
                                //and we don't have to specify the typename for certain endpoints

                                const keys = _.keys(data)

                                if (keys.length > 1) {
                                    logger.debug(
                                        'Multiple nesting in the response json object',
                                    )
                                }

                                return data[_.first(keys)]
                            })
                        }
                        return response
                    },
                },
            },
            fieldNameNormalizer: key => _.camelCase(key),
        }),
        errorLink,
        new BatchHttpLink({
            uri: getenv('GRAPHQL_API_BASE'),
            batchInterval: 10,
            fetch: window.fetch,
            credentials: 'include',
        }),
    ])
}
export default class ApiBuilder {
    static create({ appName, loadDrift = false } = {}) {
        const api = new ApiClient()
        const heapApi = new HeapApi()
        const driftApi = new DriftApi({
            appId: getenv('DRIFT_APP_ID'),
        })

        const munchkinApi = new MunchkinApi()
        const storageEngine = new LocalStorageEngine(appName)
        let client = null

        const middlewares = [
            thunk.withExtraArgument({
                api,
                // @note: Have to this, b/c there is now a chicken/egg problem
                // with store and the client
                get client() {
                    return client
                },
            }),
            createScrollMiddleware(),
            driftMiddleware(driftApi),
            createRouterTrackingMiddleware(),
        ]

        const analyticsHandlers = [
            createDriftHandler(driftApi, loadDrift),
            createHeapHandler(heapApi),
            createMunchkinHandler(munchkinApi),
            createFictivHandler(api, {
                product: appName,
                version: __RELEASE_ID__,
            }),
        ]

        // Prevent undefined passed to handler, prevent failure in cypress
        const APPCUES_ID = getenv('APPCUES_APP_ID')

        if (APPCUES_ID && !window.Cypress) {
            analyticsHandlers.push(
                createAppcuesHandler(
                    new AppcuesApi({
                        win: window,
                        appId: APPCUES_ID,
                    }),
                ),
            )
        }

        // Analytics middleware must come after any promise middlewares like thunk or our API.
        middlewares.push(createAnalyticsMiddleware(analyticsHandlers))

        // Set up syncing to localStorage for certain subpaths in our redux store.
        middlewares.push(
            createStorageMiddleware(
                storageEngine,
                [...SAVE_TYPES],
                ['preferences'],
            ),
        )

        if (getenv('USE_LOGGER')) {
            middlewares.push(
                createLogger({
                    collapsed: true,
                    timestamp: false,
                }),
            )
        }

        const middleware = applyMiddleware(...middlewares)

        const reducer = () =>
            combineReducers({
                auth,
                preferences,
                estimates,
                identity,
                modals,
                quotes,
                form,
                ...qmrViewerReducer,
                graphql: graphqlReducer,
                dfmLayers: dfmLayersReducer,
                dfmMessages: dfmMessagesReducer,
                imDfm: imDfmReducer,
                quoteDetail: quoteDetailReducer,
                userDetails: userDetailsReducer,
                queryRefactor: queryRefactorReducer,
                quoteQllt: quoteQlltReducer,
                unsavedImDfm3dSlides: unsavedImDfm3dSlidesReducer,
                quoteModelRevision: quoteModelRevisionReducer,
                shippingAddressHTQuote: shippingAddressHTQuoteReducer,
            })
        const composeEnhancers =
            !isProduction && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
                ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
                      actionsBlacklist: 'qmr-viewer',
                  })
                : compose
        const store = createStore(
            reducer(),
            storageEngine.load(),
            composeEnhancers(middleware),
        )

        const defaultOptions = {
            query: {
                // Appears to be broken: https://github.com/apollographql/apollo-client/issues/3880
                // fetchPolicy: 'cache-and-network',
            },
        }

        const apolloCache = new InMemoryCache(introspectionQueryResultData)

        client = new ApolloClient({
            link: createApolloLink({ store }),
            cache: apolloCache,
            defaultOptions,
            name: `js:${appName}`,
            version: __RELEASE_ID__,
            connectToDevTools: !isProduction,
        })

        api.catchError(error => {
            if (
                error.response &&
                error.response.status &&
                error.response.status === 401 //eslint-disable-line no-magic-numbers
            ) {
                return store.dispatch(ModalSessionTimedOut.open())
            }
            throw error
        })

        return {
            api, // RESTful API
            store, // redux API
            client, // graphql API
            driftApi, // third party chat API
            munchkinApi, // Marketo tracking API
            heapApi, // Heap analytics API
        }
    }
}
